import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { CalculatorLayout } from '@pages/CalculatorPage/CalculatorLayout/CalculatorLayout';
import { CalculatorBackButton } from '@components/common/CalculatorBackButton/CalculatorBackButton';
import { CalculatorSelect } from '@components/common/CalculatorSelect/CalculatorSelect';
import { CalculatorSubmitButton } from '@components/common/CalculatorSubmitButton/CalculatorSubmitButton';
import { PayeEmployedFields } from '@pages/CalculatorPage/ApplicantDetailsPage/PayeEmployedFields/PayeEmployedFields';
import { SalariedDirectorFields } from '@pages/CalculatorPage/ApplicantDetailsPage/SalariedDirectorFields/SalariedDirectorFields';
import { SoleTraderFields } from '@pages/CalculatorPage/ApplicantDetailsPage/SoleTraderFields/SoleTraderFields';
import { ContractorFields } from '@pages/CalculatorPage/ApplicantDetailsPage/ContractorFields/ContractorFields';
import { CalculatorFormControl } from '@components/common/CalculatorFormControl/CalculatorFormControl';
import { OtherIncome } from '@pages/CalculatorPage/ApplicantDetailsPage/OtherIncome/OtherIncome';
import { BenefitsAndCredits } from '@pages/CalculatorPage/ApplicantDetailsPage/BenefitsAndCredits/BenefitsAndCredits';
import { applicantDetailsQuestions } from './questions';
import { applicantDefaultValues, incomeDefaultValues } from '../helpers';
import { AffordabilityCalculatorForm, CalculatorPages, EmploymentTypesEnum, MainTypesEnum } from '../types';
import { useSearchParams } from 'react-router-dom';
import { ApplicantDetailsPageProps } from '../ApplicantsWrapper/types';
import { saveAffordabilityFormToLocalStorage, setLastAffordabilityCalcPage } from '@utils/localStorage';
import { employmentTypesWithIncome } from './helpers';
import { scrollToTop } from '@utils/general';
import { TimeTradingEnum } from './SalariedDirectorFields/types';

export const ApplicantDetailsPage: FC<ApplicantDetailsPageProps> = ({ applicantNumber }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const form = useFormContext<AffordabilityCalculatorForm>();
  const formFields = form.watch();
  const intl = useIntl();
  const { employmentType: employmentTypeQuestions, secondIncome, error } = applicantDetailsQuestions;

  const getIncomeFieldsByEmploymentType = (employmentType: string | null, incomeType: MainTypesEnum) => {
    const isTimeTradingMoreThanTwoYears =
      formFields.applicants?.[applicantNumber]?.[incomeType]?.timeTrading === TimeTradingEnum.moreThanTwoYears;
    const incomeFieldsProps = { isTimeTradingMoreThanTwoYears, applicantNumber, incomeType };

    switch (employmentType) {
      case EmploymentTypesEnum.payeEmployed:
        return <PayeEmployedFields incomeType={incomeType} applicantNumber={applicantNumber} />;
      case EmploymentTypesEnum.salariedDirector:
        return <SalariedDirectorFields {...incomeFieldsProps} />;
      case EmploymentTypesEnum.soleTrader:
        return <SoleTraderFields {...incomeFieldsProps} />;
      case EmploymentTypesEnum.contractor:
        return <ContractorFields {...incomeFieldsProps} />;
      case EmploymentTypesEnum.partnership:
        return <SoleTraderFields {...incomeFieldsProps} />;
      case EmploymentTypesEnum.unemployed:
      case EmploymentTypesEnum.retired:
      default:
        return null;
    }
  };

  const currentApplicantDetails = formFields.applicants[applicantNumber];

  const primaryEmploymentType = currentApplicantDetails?.primaryIncome?.employmentType ?? null;
  const primaryIncomeFields = getIncomeFieldsByEmploymentType(primaryEmploymentType, MainTypesEnum.primaryIncome);
  const isSecondIncomeOptionVisible = primaryEmploymentType
    ? employmentTypesWithIncome.includes(primaryEmploymentType)
    : false;
  const isSecondIncome = currentApplicantDetails?.isSecondIncome === 'yes';
  const secondEmploymentType = currentApplicantDetails?.secondIncome?.employmentType ?? null;
  const secondIncomeFields = getIncomeFieldsByEmploymentType(secondEmploymentType, MainTypesEnum.secondIncome);
  const isOtherIncomeVisible = isSecondIncomeOptionVisible;

  const erasePrimaryIncomeData = () => {
    form.setValue(`applicants[${applicantNumber}]` as any, applicantDefaultValues);
    form.setValue(`applicants[${applicantNumber}].primaryIncome.employmentType` as any, primaryEmploymentType);
  };

  const eraseSecondIncomeData = () => {
    form.setValue(`applicants[${applicantNumber}].secondIncome` as any, incomeDefaultValues);
    form.setValue(`applicants[${applicantNumber}].secondIncome.employmentType` as any, secondEmploymentType);
  };

  const goToNextPage = async () => {
    const triggerResultPromise = form.trigger(`applicants[${applicantNumber}]` as keyof AffordabilityCalculatorForm, {
      shouldFocus: true,
    });
    const isNoErrors = await triggerResultPromise;

    if (isNoErrors) {
      form.setValue(`applicants[${applicantNumber}].isFilled` as any, true);
      saveAffordabilityFormToLocalStorage(formFields);

      if (applicantNumber === formFields.applicants.length - 1) {
        setLastAffordabilityCalcPage(CalculatorPages.outgoings);
        searchParams.set('section', CalculatorPages.outgoings);
      } else {
        searchParams.set('applicantNumber', String(applicantNumber + 1));
      }

      setSearchParams(searchParams);
      scrollToTop();
    }
  };

  const goToPreviousPage = () => {
    if (applicantNumber === 0) {
      searchParams.set('section', CalculatorPages.financeDetails);
      setSearchParams(searchParams);
    } else {
      searchParams.set('applicantNumber', String(applicantNumber - 1));
      setSearchParams(searchParams);
    }
    scrollToTop();
  };

  return (
    <CalculatorLayout currentSectionNumber={2} additionalClasses={'after:w-2/5'}>
      <h2
        className={
          'xs:hidden text-dark font-satoshi-black xs:text-xl font-black md:mb-10 md:mt-16 md:block lg:text-2xl'
        }
      >
        Applicant {applicantNumber + 1}
      </h2>

      <CalculatorSelect
        name={`applicants[${applicantNumber}].primaryIncome.employmentType`}
        key={`applicants[${applicantNumber}].primaryIncome.employmentType`}
        titleKey={employmentTypeQuestions.titleKey}
        placeholderKey={employmentTypeQuestions.placeholderKey}
        rules={{ required: intl.formatMessage({ id: error.required }) }}
        options={employmentTypeQuestions.options}
        className={'xs:my-5 md:mt-0 md:mb-10'}
        onChangeEffect={erasePrimaryIncomeData}
      />
      {primaryIncomeFields}
      {isSecondIncomeOptionVisible && (
        <>
          <hr className={'border-fog xs:mb-6 border border-t-0 border-solid md:mb-12'} />
          <CalculatorFormControl
            className={'xs:mb-5 md:mb-10'}
            name={`applicants[${applicantNumber}].isSecondIncome`}
            titleKey={secondIncome.titleKey}
            radioOptions={secondIncome.options}
            isWide
          />
          {isSecondIncome && (
            <>
              <CalculatorSelect
                name={`applicants[${applicantNumber}].secondIncome.employmentType`}
                titleKey={employmentTypeQuestions.titleKey}
                placeholderKey={employmentTypeQuestions.placeholderKey}
                rules={{ required: intl.formatMessage({ id: error.required }) }}
                options={employmentTypeQuestions.options}
                className={'xs:mb-5 md:mb-10'}
                onChangeEffect={eraseSecondIncomeData}
              />
              {secondIncomeFields}
            </>
          )}
          <hr className={'border-fog xs:mb-6 border border-t-0 border-solid md:mb-12'} />
        </>
      )}
      {isOtherIncomeVisible && (
        <>
          <OtherIncome applicantNumber={applicantNumber} />
          <BenefitsAndCredits applicantNumber={applicantNumber} />
        </>
      )}

      <div className={'xs:flex-col-reverse xs:mt-10 flex w-full justify-between md:flex-row'}>
        <CalculatorBackButton onClick={goToPreviousPage} />
        <div className={'h-8 w-full md:hidden'}></div>
        <CalculatorSubmitButton onClick={goToNextPage} />
      </div>
    </CalculatorLayout>
  );
};
